import { useState } from 'react';

import {
  useAddShares,
  useContract,
  useDeleteShare,
  useImportSharesCsv,
  useShareMetrics,
  useShares,
  useUpdateContract,
  useUpdateShare,
} from '../../api/hooks';
import type { SaleType, ShareBasis, ShareView } from '../../api/types';
import {
  Button,
  DataTable,
  FormField,
  Select,
  SelectWithCaption,
  TextInput,
  useToast,
} from '../../components/DesignSystem';
import type { DataTableColumn } from '../../components/DesignSystem';
import { ADVANCE_MODES, findAdvanceMode } from '../../lib/advanceModes';
import { errorMessage } from '../../lib/errors';
import { formatDate, formatNumber, formatPercent } from '../../lib/format';
import { CsvImportBox } from './CsvImportBox';
import { EMPTY_PARTY_VALUE, NEW_PARTY_OPTION, PartyField, usePartyResolver } from './PartyField';
import type { PartyFieldValue } from './PartyField';
import { SALE_TYPE_OPTIONS } from './saleTypes';

/** Deficit Mode isn't specified by the client's Advance Mode helper text — kept as its own option set. */
const DEFICIT_MODES = ['Consolidated', 'Per Party'];

interface ShareForm {
  party: PartyFieldValue;
  basis: ShareBasis;
  value: string;
  tier: string;
  territory: string;
  role: string;
  media: string;
  saleType: string;
  isAiRevenue: boolean;
  level: string;
  distributionCommissionPct: string;
  distributionExpensePct: string;
  advance: string;
  deficit: string;
  sua: string;
  suaEligibleFrom: string;
  isPerformer: boolean;
  isWriter: boolean;
  licenseStart: string;
  licenseEnd: string;
  notes: string;
}

const EMPTY_SHARE_FORM: ShareForm = {
  party: EMPTY_PARTY_VALUE,
  basis: 'percentage',
  value: '',
  tier: '',
  territory: '',
  role: '',
  media: '',
  saleType: '',
  isAiRevenue: false,
  level: '',
  distributionCommissionPct: '',
  distributionExpensePct: '',
  advance: '',
  deficit: '',
  sua: '',
  suaEligibleFrom: '',
  isPerformer: false,
  isWriter: false,
  licenseStart: '',
  licenseEnd: '',
  notes: '',
};

const shareToForm = (row: ShareView): ShareForm => ({
  party: { choice: row.partyId, newName: '' },
  basis: row.basis,
  value: String(row.value),
  tier: row.tier ?? '',
  territory: row.territory ?? '',
  role: row.role ?? '',
  media: row.media ?? '',
  saleType: row.saleType ?? '',
  isAiRevenue: row.isAiRevenue,
  level: row.level ?? '',
  distributionCommissionPct: row.distributionCommissionPct?.toString() ?? '',
  distributionExpensePct: row.distributionExpensePct?.toString() ?? '',
  advance: row.advance?.toString() ?? '',
  deficit: row.deficit?.toString() ?? '',
  sua: row.sua?.toString() ?? '',
  suaEligibleFrom: row.suaEligibleFrom ?? '',
  isPerformer: row.isPerformer,
  isWriter: row.isWriter,
  licenseStart: row.licenseStart ?? '',
  licenseEnd: row.licenseEnd ?? '',
  notes: '',
});

const buildShareColumns = (
  onEdit: (row: ShareView) => void,
  onDelete: (row: ShareView) => void,
  deletingShareId: string | null,
): DataTableColumn<ShareView>[] => [
  { key: 'partyName', header: 'Party' },
  { key: 'role', header: 'Role', render: (row) => row.role ?? '—' },
  { key: 'territory', header: 'Territory', render: (row) => row.territory ?? 'All' },
  { key: 'saleType', header: 'Sale type', render: (row) => row.saleType ?? 'All' },
  { key: 'level', header: 'Level', render: (row) => row.level ?? '1' },
  {
    key: 'value',
    header: 'Net Receipts %',
    align: 'right',
    render: (row) =>
      row.basis === 'percentage' ? formatPercent(row.value) : formatNumber(row.value),
  },
  {
    key: 'license',
    header: 'License Window',
    render: (row) =>
      row.licenseStart || row.licenseEnd
        ? `${formatDate(row.licenseStart)} – ${formatDate(row.licenseEnd)}`
        : '—',
  },
  {
    key: 'actions',
    header: '',
    render: (row) => (
      <div className="rfp-row-actions">
        <Button onClick={() => onEdit(row)} type="button" variant="secondary">
          Edit
        </Button>
        <Button
          disabled={deletingShareId === row.id}
          onClick={() => onDelete(row)}
          type="button"
          variant="secondary"
        >
          Delete
        </Button>
      </div>
    ),
  },
];

interface BackendShareTableProps {
  contractId: string;
}

/**
 * Backend Share by Parties — a compact summary table (Party, Role,
 * Territory, Net Receipts %, License Window) plus a single grouped
 * edit/add panel covering every field, so no column is hidden behind
 * horizontal scroll and no data requires more than one click to reach.
 */
export function BackendShareTable({ contractId }: BackendShareTableProps) {
  const { showToast } = useToast();
  const contract = useContract(contractId);
  const shares = useShares(contractId);
  const metrics = useShareMetrics(contractId);

  const updateContract = useUpdateContract();
  const addShares = useAddShares();
  const updateShare = useUpdateShare();
  const deleteShare = useDeleteShare();
  const importCsv = useImportSharesCsv();
  const resolveParty = usePartyResolver();

  const [form, setForm] = useState<ShareForm>(EMPTY_SHARE_FORM);
  const [editingShareId, setEditingShareId] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [deletingShareId, setDeletingShareId] = useState<string | null>(null);

  const set = (patch: Partial<ShareForm>) => setForm((current) => ({ ...current, ...patch }));

  function handleEdit(row: ShareView) {
    setForm(shareToForm(row));
    setEditingShareId(row.id);
  }

  function handleCancel() {
    setForm(EMPTY_SHARE_FORM);
    setEditingShareId(null);
  }

  async function handleDelete(row: ShareView) {
    if (!window.confirm(`Delete the ${row.partyName} share?`)) {
      return;
    }

    setDeletingShareId(row.id);

    try {
      await deleteShare.mutateAsync({ contractId, shareId: row.id });
      showToast('Share deleted');
      if (editingShareId === row.id) {
        handleCancel();
      }
    } catch (error) {
      showToast(errorMessage(error, 'Could not delete share'), 'error');
    } finally {
      setDeletingShareId(null);
    }
  }

  async function handleSubmit() {
    const numericValue = Number(form.value);

    if (
      form.party.choice === '' ||
      (form.party.choice === NEW_PARTY_OPTION && form.party.newName.trim() === '')
    ) {
      showToast('Choose or name a party for the share', 'error');
      return;
    }
    if (!Number.isFinite(numericValue) || numericValue <= 0) {
      showToast('Enter a positive Net Receipts Share value', 'error');
      return;
    }
    if (form.level.trim() !== '' && !/^(?:[1-9]\d?|100)$/.test(form.level.trim())) {
      showToast('Level must be an integer from 1 to 100', 'error');
      return;
    }

    setIsSaving(true);

    try {
      const partyId = await resolveParty(form.party);
      if (partyId === undefined) {
        return;
      }

      const optionalNumber = (value: string) => (value.trim() === '' ? undefined : Number(value));
      const nullableNumber = (value: string) => (value.trim() === '' ? null : Number(value));
      const optionalString = (value: string) => (value.trim() === '' ? undefined : value.trim());

      const shared = {
        partyId,
        basis: form.basis,
        value: numericValue,
        tier: optionalString(form.tier),
        territory: optionalString(form.territory),
        role: optionalString(form.role),
        media: optionalString(form.media),
        saleType: form.saleType === '' ? undefined : (form.saleType as SaleType),
        isAiRevenue: form.isAiRevenue,
        level: optionalString(form.level),
        advance: optionalNumber(form.advance),
        deficit: optionalNumber(form.deficit),
        sua: optionalNumber(form.sua),
        suaEligibleFrom: optionalString(form.suaEligibleFrom),
        isPerformer: form.isPerformer,
        isWriter: form.isWriter,
        licenseStart: optionalString(form.licenseStart),
        licenseEnd: optionalString(form.licenseEnd),
      };

      if (editingShareId !== null) {
        await updateShare.mutateAsync({
          contractId,
          shareId: editingShareId,
          input: {
            ...shared,
            distributionCommissionPct: nullableNumber(form.distributionCommissionPct),
            distributionExpensePct: nullableNumber(form.distributionExpensePct),
          },
        });
        showToast('Share updated');
      } else {
        await addShares.mutateAsync({
          contractId,
          shares: [
            {
              ...shared,
              distributionCommissionPct: optionalNumber(form.distributionCommissionPct),
              distributionExpensePct: optionalNumber(form.distributionExpensePct),
            },
          ],
        });
        showToast('Share added');
      }
      handleCancel();
    } catch (error) {
      showToast(
        errorMessage(
          error,
          editingShareId !== null ? 'Could not update share' : 'Could not add share',
        ),
        'error',
      );
    } finally {
      setIsSaving(false);
    }
  }

  async function handleModeChange(field: 'advanceMode' | 'deficitMode', value: string) {
    try {
      await updateContract.mutateAsync({ contractId, input: { [field]: value } });
    } catch (error) {
      showToast(errorMessage(error, 'Could not update mode'), 'error');
    }
  }

  return (
    <section className="rfp-card">
      <div className="rfp-card__head">
        <div>
          <h2 className="rfp-card__title">Back End Share by Parties</h2>
          <p className="rfp-card__caption">Who participates in net receipts, and on what basis.</p>
        </div>
        <div className="rfp-row">
          <SelectWithCaption
            caption={findAdvanceMode(contract.data?.advanceMode)?.caption ?? ''}
            htmlFor="advance-mode"
            label="Advance Mode"
            onChange={(value) => handleModeChange('advanceMode', value)}
            value={findAdvanceMode(contract.data?.advanceMode)?.key ?? ''}
          >
            <option value="">—</option>
            {ADVANCE_MODES.map((mode) => (
              <option key={mode.key} value={mode.key}>
                {mode.label}
              </option>
            ))}
          </SelectWithCaption>
          <FormField htmlFor="deficit-mode" label="Deficit Mode">
            <Select
              id="deficit-mode"
              onChange={(event) => handleModeChange('deficitMode', event.target.value)}
              value={contract.data?.deficitMode ?? ''}
            >
              <option value="">—</option>
              {DEFICIT_MODES.map((mode) => (
                <option key={mode} value={mode}>
                  {mode}
                </option>
              ))}
            </Select>
          </FormField>
        </div>
      </div>

      {shares.isLoading ? (
        <p className="rfp-loading">Loading shares…</p>
      ) : (
        <DataTable
          columns={buildShareColumns(handleEdit, handleDelete, deletingShareId)}
          emptyMessage="No shares yet — add one below or import a CSV"
          rowKey={(row) => row.id}
          rows={shares.data ?? []}
        />
      )}

      <div className="rfp-metric-strip">
        <div className="rfp-metric-strip__item">
          <span className="rfp-metric-strip__label">% Allocated / Revenue Pool</span>
          <span className="rfp-metric-strip__value">
            {formatPercent(metrics.data?.totalPercentAllocated)}
          </span>
        </div>
        <div className="rfp-metric-strip__item">
          <span className="rfp-metric-strip__label">Unallocated</span>
          <span className="rfp-metric-strip__value">
            {formatPercent(metrics.data?.unallocatedPercent)}
          </span>
        </div>
        <div className="rfp-metric-strip__item">
          <span className="rfp-metric-strip__label">Parties</span>
          <span className="rfp-metric-strip__value">{formatNumber(metrics.data?.partyCount)}</span>
        </div>
        <div className="rfp-metric-strip__item">
          <span className="rfp-metric-strip__label">Revenue Pools</span>
          <span className="rfp-metric-strip__value">
            {formatNumber(metrics.data?.allocationRevenuePools.length)}
          </span>
        </div>
      </div>

      <div className="rfp-card__head" style={{ marginTop: 24 }}>
        <div>
          <h3 className="rfp-card__title">{editingShareId !== null ? 'Edit Share' : 'Add Row'}</h3>
          <p className="rfp-card__caption">Identity</p>
        </div>
      </div>
      <div className="rfp-form-grid">
        <PartyField id="share-party" onChange={(party) => set({ party })} value={form.party} />
        <FormField htmlFor="share-role" label="Role">
          <TextInput
            id="share-role"
            onChange={(event) => set({ role: event.target.value })}
            placeholder="e.g. Producer, Broadcaster"
            value={form.role}
          />
        </FormField>
        <FormField htmlFor="share-territory" label="Territory">
          <TextInput
            id="share-territory"
            onChange={(event) => set({ territory: event.target.value })}
            placeholder="e.g. UK"
            value={form.territory}
          />
        </FormField>
        <FormField htmlFor="share-media" label="Media">
          <TextInput
            id="share-media"
            onChange={(event) => set({ media: event.target.value })}
            placeholder="e.g. Television, SVOD"
            value={form.media}
          />
        </FormField>
        <FormField htmlFor="share-sale-type" label="Sale Type / License Type">
          <Select
            id="share-sale-type"
            onChange={(event) => set({ saleType: event.target.value })}
            value={form.saleType}
          >
            <option value="">—</option>
            {SALE_TYPE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </FormField>
        <FormField htmlFor="share-level" label="Level">
          <TextInput
            id="share-level"
            min="1"
            max="100"
            onChange={(event) => set({ level: event.target.value })}
            placeholder="e.g. 1"
            step="1"
            type="number"
            value={form.level}
          />
        </FormField>
        <FormField htmlFor="share-tier" label="Tier (optional)">
          <TextInput
            id="share-tier"
            onChange={(event) => set({ tier: event.target.value })}
            placeholder="e.g. UK secondary sales"
            value={form.tier}
          />
        </FormField>
        <label className="rfp-check">
          <input
            checked={form.isAiRevenue}
            onChange={(event) => set({ isAiRevenue: event.target.checked })}
            type="checkbox"
          />
          AI Revenue (applies to all sale types)
        </label>
      </div>

      <p className="rfp-card__caption" style={{ marginTop: 16 }}>
        Financial Terms
      </p>
      <div className="rfp-form-grid">
        <FormField htmlFor="share-basis" label="Basis">
          <Select
            id="share-basis"
            onChange={(event) => set({ basis: event.target.value as ShareBasis })}
            value={form.basis}
          >
            <option value="percentage">Percentage</option>
            <option value="points">Points</option>
            <option value="fixed">Fixed</option>
          </Select>
        </FormField>
        <FormField htmlFor="share-value" label="Net Receipts Share %">
          <TextInput
            id="share-value"
            min="0"
            onChange={(event) => set({ value: event.target.value })}
            placeholder="e.g. 25"
            step="0.01"
            type="number"
            value={form.value}
          />
        </FormField>
        <FormField htmlFor="share-commission" label="Distribution Commission %">
          <TextInput
            id="share-commission"
            min="0"
            onChange={(event) => set({ distributionCommissionPct: event.target.value })}
            step="0.01"
            type="number"
            value={form.distributionCommissionPct}
          />
        </FormField>
        <FormField htmlFor="share-expense" label="Distribution Expense %">
          <TextInput
            id="share-expense"
            min="0"
            onChange={(event) => set({ distributionExpensePct: event.target.value })}
            step="0.01"
            type="number"
            value={form.distributionExpensePct}
          />
        </FormField>
        <FormField htmlFor="share-advance" label="Advance">
          <TextInput
            id="share-advance"
            min="0"
            onChange={(event) => set({ advance: event.target.value })}
            step="0.01"
            type="number"
            value={form.advance}
          />
        </FormField>
        <FormField htmlFor="share-deficit" label="Deficit">
          <TextInput
            id="share-deficit"
            min="0"
            onChange={(event) => set({ deficit: event.target.value })}
            step="0.01"
            type="number"
            value={form.deficit}
          />
        </FormField>
        <FormField htmlFor="share-sua" label="SUA">
          <TextInput
            id="share-sua"
            min="0"
            onChange={(event) => set({ sua: event.target.value })}
            step="0.01"
            type="number"
            value={form.sua}
          />
        </FormField>
        <FormField htmlFor="share-sua-eligible-from" label="SUA eligible from">
          <TextInput
            id="share-sua-eligible-from"
            onChange={(event) => set({ suaEligibleFrom: event.target.value })}
            type="date"
            value={form.suaEligibleFrom}
          />
        </FormField>
      </div>

      <p className="rfp-card__caption" style={{ marginTop: 16 }}>
        Residual Flags &amp; License Window
      </p>
      <div className="rfp-form-grid">
        <label className="rfp-check">
          <input
            checked={form.isPerformer}
            onChange={(event) => set({ isPerformer: event.target.checked })}
            type="checkbox"
          />
          Performer
        </label>
        <label className="rfp-check">
          <input
            checked={form.isWriter}
            onChange={(event) => set({ isWriter: event.target.checked })}
            type="checkbox"
          />
          Writer
        </label>
        <FormField htmlFor="share-license-start" label="License Start">
          <TextInput
            id="share-license-start"
            onChange={(event) => set({ licenseStart: event.target.value })}
            type="date"
            value={form.licenseStart}
          />
        </FormField>
        <FormField htmlFor="share-license-end" label="License End">
          <TextInput
            id="share-license-end"
            onChange={(event) => set({ licenseEnd: event.target.value })}
            type="date"
            value={form.licenseEnd}
          />
        </FormField>
      </div>

      <div className="rfp-wizard-actions">
        <Button disabled={isSaving} onClick={handleSubmit} type="button">
          {isSaving ? 'Saving…' : editingShareId !== null ? 'Save' : '+ Add Row'}
        </Button>
        {editingShareId !== null && (
          <Button onClick={handleCancel} type="button" variant="secondary">
            Cancel
          </Button>
        )}
      </div>

      <div className="rfp-card__head" style={{ marginTop: 24 }}>
        <div>
          <h3 className="rfp-card__title">Or Import Shares from CSV</h3>
        </div>
      </div>
      <CsvImportBox
        entity="shares"
        onImport={(csvText, fileName) => importCsv.mutateAsync({ contractId, csvText, fileName })}
        successMessage="Shares imported from CSV"
      />
    </section>
  );
}
